import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import { Close, ThumbDown } from '@mui/icons-material';
import { FeedbackConfig, FeedbackState } from '../types/feedback';

interface NegativeFeedbackFlyoutProps {
  open: boolean;
  messageId: string;
  feedbackConfig: FeedbackConfig;
  existingFeedback?: FeedbackState | null;
  onSubmit: (reasons: string[], comment?: string) => Promise<void>;
  onClose: () => void;
}

const NegativeFeedbackFlyout: React.FC<NegativeFeedbackFlyoutProps> = ({
  open,
  feedbackConfig,
  existingFeedback,
  onSubmit,
  onClose
}) => {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with existing feedback data
  useEffect(() => {
    if (existingFeedback?.type === 'negative') {
      setSelectedReasons(existingFeedback.reasons || []);
      setComment(existingFeedback.comment || '');
    } else {
      setSelectedReasons([]);
      setComment('');
    }
  }, [existingFeedback, open]);
  const handleReasonChange = (reasonValue: string) => {
    if (feedbackConfig.negativeReasons.multiSelect) {
      setSelectedReasons(prev => 
        prev.includes(reasonValue)
          ? prev.filter(r => r !== reasonValue)
          : [...prev, reasonValue]
      );
    } else {
      // Single select mode
      setSelectedReasons([reasonValue]);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(
        selectedReasons,
        feedbackConfig.commentConfig.enabled && comment.trim() 
          ? comment.trim() 
          : undefined
      );
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const canSubmit = (): boolean => {
    // Must have at least one reason selected
    if (selectedReasons.length === 0) {
      return false;
    }

    // If comment is required, must have comment
    if (feedbackConfig.commentConfig.enabled && 
        feedbackConfig.commentConfig.required && 
        !comment.trim()) {
      return false;
    }

    return true;
  };

  const getSubmitButtonText = (): string => {
    if (existingFeedback?.type === 'negative') {
      return feedbackConfig.text.updateFeedbackButton || 'Update Feedback';
    }
    return feedbackConfig.text.submitFeedbackButton || 'Submit Feedback';
  };
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          maxWidth: '90vw',
          bgcolor: feedbackConfig.theme.flyoutBackground,
          color: feedbackConfig.theme.textColor
        }
      }}
      ModalProps={{
        keepMounted: false
      }}
    >
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: feedbackConfig.theme.flyoutBackground
      }}>        {/* Header */}
        <Box sx={{ 
          p: 1.5, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: feedbackConfig.theme.flyoutBorderColor || 'divider',
          bgcolor: feedbackConfig.theme.flyoutBackground || 'rgba(211, 47, 47, 0.04)',
          color: feedbackConfig.theme.flyoutTitleColor || '#d32f2f',
          minHeight: 48
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ThumbDown sx={{ 
              color: feedbackConfig.theme.flyoutTitleColor || '#d32f2f',
              fontSize: '1.2rem'
            }} />
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600,
              color: feedbackConfig.theme.flyoutTitleColor || '#d32f2f',
              fontSize: '1rem'
            }}>
              {feedbackConfig.text.negativeFlyoutTitle || 'Help us improve'}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={isSubmitting}
            size="small"
            sx={{ 
              color: feedbackConfig.theme.flyoutTitleColor || '#d32f2f',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.08)'
              }
            }}
          >
            <Close />
          </IconButton>
        </Box>        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {/* Subtitle */}
          {feedbackConfig.text.negativeFlyoutSubtitle && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ mb: 2.5, color: `${feedbackConfig.theme.textColor}80` }}
            >
              {feedbackConfig.text.negativeFlyoutSubtitle}
            </Typography>
          )}

          {/* Reasons Section */}
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            {feedbackConfig.text.reasonsLabel || 'What went wrong?'}
            {!feedbackConfig.negativeReasons.multiSelect && (
              <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                (Select one)
              </Typography>
            )}
            {feedbackConfig.negativeReasons.multiSelect && (
              <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                (Select all that apply)
              </Typography>
            )}
          </Typography><FormGroup sx={{ mb: 2 }}>
          {feedbackConfig.negativeReasons.options.map((reasonOption) => (
            <FormControlLabel
              key={reasonOption.value}
              control={
                <Checkbox
                  checked={selectedReasons.includes(reasonOption.value)}
                  onChange={() => handleReasonChange(reasonOption.value)}
                  disabled={isSubmitting}
                  sx={{
                    color: feedbackConfig.theme.negativeColor,
                    '&.Mui-checked': {
                      color: feedbackConfig.theme.selectedColor
                    }
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: feedbackConfig.theme.textColor }}>
                  {reasonOption.display}
                </Typography>
              }
              sx={{ ml: 0 }}
            />
          ))}
        </FormGroup>

        {/* Comment Section */}
        {feedbackConfig.commentConfig.enabled && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {feedbackConfig.commentConfig.label || 'Additional feedback'}
              {feedbackConfig.commentConfig.required && (
                <Typography component="span" sx={{ color: feedbackConfig.theme.negativeColor, ml: 0.5 }}>
                  *
                </Typography>
              )}
            </Typography>
            
            {feedbackConfig.commentConfig.placeholder && (
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block', 
                  mb: 1, 
                  color: `${feedbackConfig.theme.textColor}60`,
                  fontStyle: 'italic'
                }}
              >
                {feedbackConfig.commentConfig.placeholder}
              </Typography>
            )}

            <TextField
              multiline
              rows={3}
              fullWidth
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={isSubmitting}
              placeholder={feedbackConfig.commentConfig.placeholder || 'Tell us more...'}
              inputProps={{
                maxLength: feedbackConfig.commentConfig.maxLength || 500
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: feedbackConfig.theme.textColor,
                  '& fieldset': {
                    borderColor: `${feedbackConfig.theme.textColor}30`
                  },
                  '&:hover fieldset': {
                    borderColor: `${feedbackConfig.theme.textColor}50`
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: feedbackConfig.theme.selectedColor
                  }
                },
                '& .MuiInputLabel-root': {
                  color: `${feedbackConfig.theme.textColor}70`
                }
              }}
            />
            
            {feedbackConfig.commentConfig.maxLength && (
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block', 
                  textAlign: 'right', 
                  mt: 0.5,
                  color: `${feedbackConfig.theme.textColor}60`
                }}
              >
                {comment.length}/{feedbackConfig.commentConfig.maxLength}
              </Typography>
            )}
          </Box>        )}        </Box>

        {/* Footer/Actions */}
        <Box sx={{ 
          px: 2, 
          py: 1.5, 
          borderTop: '1px solid',
          borderColor: feedbackConfig.theme.flyoutBorderColor || 'divider',
          bgcolor: feedbackConfig.theme.flyoutBackground,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1
        }}>
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{
              color: `${feedbackConfig.theme.textColor}80`,
              '&:hover': {
                backgroundColor: `${feedbackConfig.theme.textColor}10`
              }
            }}
          >
            {feedbackConfig.text.cancelButton || 'Cancel'}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit() || isSubmitting}
            variant="contained"
            sx={{
              bgcolor: feedbackConfig.theme.selectedColor,
              color: '#fff',
              '&:hover': {
                bgcolor: feedbackConfig.theme.selectedColor,
                opacity: 0.9
              },
              '&:disabled': {
                bgcolor: `${feedbackConfig.theme.textColor}20`,
                color: `${feedbackConfig.theme.textColor}40`
              }
            }}
          >
            {isSubmitting ? 'Submitting...' : getSubmitButtonText()}
          </Button>
        </Box>

        {/* Footer Message */}
        <Box sx={{ 
          px: 2, 
          py: 1, 
          bgcolor: 'background.default',
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>          <Typography variant="caption" sx={{ 
            color: 'text.secondary', 
            fontStyle: 'italic',
            fontSize: '0.7rem'
          }}>
            Your feedback helps us improve the AI assistant
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default NegativeFeedbackFlyout;
